import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { Spinner } from '@monorepo/base/src/components/spinner/spinner';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { IMFPPStore } from '../../stores';
import { NotificationsPageApi } from '../../apis/notifications-page.api';
import { PausedCampaignsModal } from './paused-campaigns-modal';
import styles from './notifications-center-page.module.scss';

const placeholderSections = [
	{
		icon: 'star-01',
		title: 'Wishlist Matches',
		description: 'Campaigns matching your saved interests will appear here.',
	},
	{
		icon: 'trend-up-01',
		title: 'Top Campaigns on ManiFesto',
		description: 'High-performing campaign suggestions will appear here.',
	},
	{
		icon: 'target-03',
		title: 'Recommended for Your Traffic',
		description: 'Personalized recommendations based on your traffic will appear here.',
	},
];

export const NotificationsCenterPage: FC = () => {
	const { modalsStore } = useStores<IMFPPStore>();
	const [searchParams] = useSearchParams();
	const accountId = searchParams.get('accountId');
	const [affectedCount, setAffectedCount] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setIsLoading(true);
		setError(null);
		NotificationsPageApi.fetchCampaignAvailabilityAffectedCount(accountId)
			.then(setAffectedCount)
			.catch((err: unknown) => {
				if (isAbortError(err as IAskError)) {
					return;
				}
				setError('Could not load notification center data. Please refresh the page and try again.');
			})
			.finally(() => setIsLoading(false));
	}, [accountId]);

	const openPausedCampaignsModal = () => {
		modalsStore.addModal({
			children: <PausedCampaignsModal accountId={accountId} />,
		});
	};

	return (
		<div className={styles.container}>
			<header className={styles.header}>
				<div>
					<h1 className={styles.title}>Notification Center</h1>
					<p className={styles.subtitle}>Important updates and new opportunities for your traffic</p>
				</div>
				<div className={styles.headerActions}>
					<button type="button" className={styles.linkButton}>
						Mark all as read
					</button>
					<button type="button" className={styles.preferencesButton}>
						<Icon isMFP={true} size="13px" color="#344054">
							settings-01
						</Icon>
						Notification preferences
					</button>
				</div>
			</header>

			<section className={styles.section}>
				<h2 className={styles.sectionHeader}>
					<Icon isMFP={true} size="13px" color="#667085">
						alert-triangle
					</Icon>
					Action Required ({isLoading ? '...' : affectedCount})
				</h2>
				{error ? <div className={styles.error}>{error}</div> : null}
				{isLoading ? (
					<div className={styles.loadingState}>
						<Spinner size="18px" />
						<p className={styles.stateText}>Loading action required notifications...</p>
					</div>
				) : null}
				{!isLoading && !error && affectedCount === 0 ? (
					<div className={`${styles.card} ${styles.emptyCard}`}>
						<p className={styles.cardDescription}>All of your active campaigns are currently available.</p>
					</div>
				) : null}
				{!isLoading && !error && affectedCount > 0 ? (
					<div className={styles.card}>
						<div className={styles.cardMain}>
							<span className={styles.unreadDot} aria-hidden={true} />
							<div className={styles.cardContent}>
								<h3 className={styles.cardTitle}>Some of your active campaigns are currently paused</h3>
								<p className={styles.cardDescription}>Stop sending traffic to avoid losing clicks.</p>
								<p className={styles.metaText}>{affectedCount} campaigns affected</p>
								<div className={styles.cardActions}>
									<button type="button" className={styles.primaryAction} onClick={openPausedCampaignsModal}>
										View campaigns
									</button>
									<button type="button" className={styles.textAction}>
										<Icon isMFP={true} size="12px" color="#667085">
											check
										</Icon>
										Mark as read
									</button>
									<button type="button" className={styles.textAction}>
										Dismiss
									</button>
								</div>
							</div>
						</div>
					</div>
				) : null}
			</section>

			{placeholderSections.map(section => (
				<section className={styles.section} key={section.title}>
					<h2 className={styles.sectionHeader}>
						<Icon isMFP={true} size="13px" color="#667085">
							{section.icon}
						</Icon>
						{section.title} (0)
					</h2>
					<div className={`${styles.card} ${styles.emptyCard}`}>
						<p className={styles.cardDescription}>{section.description}</p>
					</div>
				</section>
			))}
		</div>
	);
};
